import React, { useEffect, useRef, useState } from "react";

import { Check, ChevronDown } from "lucide-react";

import ChipV4 from "./ChipV4";

// value ที่เป็นตัวเลข (เช่น masterOptions) แปลงเป็น string ใน SelectV4
export type SelectV4Option = {
  value: number | string;
  text: string;
  disabled?: boolean;
  /** บรรทัดอธิบายใต้ชื่อรายการใน dropdown (issue 86636: Focus แสดงข้อความ A / I)
     ไม่ใส่ = หน้าตาเหมือนเดิม (default behavior ไม่เปลี่ยนสำหรับหน้าจออื่น) */
  preview?: string;
};

type SelectV4BaseProps = {
  id?: string;
  options: SelectV4Option[];
  placeholder?: string;
  /** พิมพ์กรองรายการได้ */
  search?: boolean;
  /** เพิ่มค่าที่ไม่มีในรายการได้ (ต้องเปิด search) */
  allowAdd?: boolean;
  /** Optional explicit add/done controls for team requests; other callers retain their behavior. */
  addControls?: boolean;
  /** multiple: แสดง chip ของทุกค่าที่เลือก (ค่าเริ่มต้น: chip แรก + "+n")
      HIS V4 ใช้กับ dropdown "เลือก Template การตรวจร่างกาย" (issue 86625) ที่ต้องมี ✖ ทีละอัน */
  allChips?: boolean;
  /** multiple: ไม่แสดงค่าที่เลือกแล้วในตัว dropdown (issue 86625 ข้อ 4.1 "ไม่แสดง Template ที่เลือกแล้ว") */
  hideSelectedInMenu?: boolean;
  size?: "md" | "sm";
  invalid?: boolean;
  disabled?: boolean;
  className?: string;
  "aria-label"?: string;
  onSearch?: (query: string) => void;
};

type SelectV4SingleProps = SelectV4BaseProps & {
  multiple?: false;
  value: string;
  onChange: (value: string) => void;
};

type SelectV4MultipleProps = SelectV4BaseProps & {
  multiple: true;
  value: string[];
  onChange: (value: string[]) => void;
};

type SelectV4Props = SelectV4SingleProps | SelectV4MultipleProps;

/** dropdown ของ HIS V4 (class hv4-sel ใน _hisv4-base.scss) */
const SelectV4 = (props: SelectV4Props) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(0);
  const [menuStyle, setMenuStyle] = useState<React.CSSProperties>({});

  const inputRef = useRef<HTMLInputElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const boxRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLUListElement>(null);

  const options = props.options.map((option) => ({ ...option, value: String(option.value) }));

  const values: string[] = Array.isArray(props.value)
    ? props.value
    : props.value
      ? [props.value]
      : [];

  const textOf = (value: string) => options.find((option) => option.value === value)?.text ?? value;

  const trimmed = query.trim();
  const added = props.multiple
    ? values
        .filter((value) => !options.some((option) => option.value === value))
        .map((value) => ({ value, text: value }))
    : [];
  const listed = [...options, ...added].filter(
    (option, index, list) =>
      list.findIndex((item) => item.value === option.value) === index &&
      option.text.toLowerCase().includes(trimmed.toLowerCase()),
  );
  const canAdd =
    !!props.allowAdd &&
    !!trimmed &&
    !values.includes(trimmed) &&
    !options.some((option) => option.text === trimmed);
  const listedForMenu =
    props.multiple && props.hideSelectedInMenu
      ? listed.filter((option) => !values.includes(option.value))
      : listed;

  const items: {
    value: string;
    text: string;
    isNew?: boolean;
    disabled?: boolean;
    preview?: string;
  }[] = canAdd
    ? [...listedForMenu, { value: trimmed, text: `เพิ่ม "${trimmed}"`, isNew: true }]
    : listedForMenu;

  const classes = [
    "hv4-sel",
    props.size === "sm" && "hv4-sel-sm",
    open && "hv4-sel-open",
    props.disabled && "hv4-sel-disabled",
    props.className,
  ];

  const singleText = !props.multiple && values[0] ? textOf(values[0]) : "";

  // Effect
  useEffect(() => {
    if (!open) {
      return;
    }

    const handleScroll = (e: Event) => {
      if (!menuRef.current?.contains(e.target as Node)) {
        handleClose();
      }
    };

    window.addEventListener("scroll", handleScroll, true);
    window.addEventListener("resize", handleClose);

    return () => {
      window.removeEventListener("scroll", handleScroll, true);
      window.removeEventListener("resize", handleClose);
    };
  }, [open]);

  // Handler
  const emit = (next: string[]) => {
    if (props.multiple) {
      (props as SelectV4MultipleProps).onChange(next);
    } else {
      (props as SelectV4SingleProps).onChange(next[0] ?? "");
    }
  };

  const focusField = () => (props.search ? inputRef.current : buttonRef.current)?.focus();

  // เมนูลอยเหนือ modal / การ์ดที่ตัดขอบ เปิดขึ้นบนเมื่อด้านล่างไม่พอ
  const placeMenu = () => {
    const rect = boxRef.current?.getBoundingClientRect();

    if (!rect) {
      return;
    }

    const below = window.innerHeight - rect.bottom - 8;
    const above = rect.top - 8;
    const isUp = below < 200 && above > below;

    setMenuStyle({
      left: rect.left,
      width: rect.width,
      maxHeight: Math.min(260, isUp ? above : below),
      ...(isUp ? { bottom: window.innerHeight - rect.top + 4 } : { top: rect.bottom + 4 }),
    });
  };

  const handleOpen = () => {
    if (props.disabled) {
      return;
    }
    placeMenu();
    const index = props.multiple
      ? 0
      : Math.max(
          0,
          options.findIndex((option) => option.value === values[0]),
        );
    setActive(index);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setQuery("");
  };

  const handlePick = (value: string) => {
    if (props.multiple) {
      emit(values.includes(value) ? values.filter((item) => item !== value) : [...values, value]);
      setQuery("");
      focusField();

      return;
    }
    emit([value]);
    handleClose();
  };

  const handleRemove = (value: string) => {
    emit(values.filter((item) => item !== value));
  };

  const handleChangeQuery = (value: string) => {
    setQuery(value);
    setActive(0);
    if (!open) {
      placeMenu();
    }
    setOpen(true);
    props.onSearch?.(value);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      if (!open) {
        handleOpen();

        return;
      }
      const count = items.length || 1;
      setActive((active + (e.key === "ArrowDown" ? 1 : -1) + count) % count);
    } else if (e.key === "Enter") {
      if (open && items[active]) {
        e.preventDefault();
        if (!items[active].disabled) {
          handlePick(items[active].value);
        }
      } else if (!open && !props.search) {
        e.preventDefault();
        handleOpen();
      }
    } else if (e.key === "Escape" && open) {
      e.stopPropagation();
      handleClose();
    } else if (e.key === "Backspace" && props.multiple && !query && values.length) {
      handleRemove(values[values.length - 1]);
    }
  };

  // Render
  return (
    <div
      className={classes.filter(Boolean).join(" ")}
      onBlur={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) {
          handleClose();
        }
      }}
    >
      <div
        ref={boxRef}
        className="hv4-sel-box"
        aria-invalid={props.invalid}
        onMouseDown={(e) => {
          if (e.target === e.currentTarget) {
            e.preventDefault();
            focusField();
            if (open) {
              handleClose();
            } else {
              handleOpen();
            }
          }
        }}
      >
        {props.multiple &&
          (props.allChips ? values : values.slice(0, 1)).map((value) => (
            <ChipV4 key={value} size="sm" tone="sky" className="hv4-sel-chip">
              <span className="hv4-sel-chip-text" title={textOf(value)}>
                {textOf(value)}
              </span>
              <button
                type="button"
                className="hv4-sel-chip-x"
                aria-label={`ลบ ${textOf(value)}`}
                disabled={props.disabled}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => handleRemove(value)}
              >
                ✕
              </button>
            </ChipV4>
          ))}
        {props.multiple && !props.allChips && values.length > 1 && (
          <span className="hv4-sel-more" title={values.slice(1).map(textOf).join(", ")}>
            +{values.length - 1}
          </span>
        )}
        {props.search ? (
          <input
            ref={inputRef}
            id={props.id}
            className="hv4-sel-input"
            role="combobox"
            aria-label={props["aria-label"]}
            aria-expanded={open}
            aria-autocomplete="list"
            autoComplete="off"
            disabled={props.disabled}
            placeholder={singleText || (values.length ? "" : props.placeholder)}
            value={open || props.multiple ? query : singleText}
            onChange={(e) => handleChangeQuery(e.target.value)}
            onFocus={handleOpen}
            onClick={() => !open && handleOpen()}
            onKeyDown={handleKeyDown}
          />
        ) : (
          <button
            ref={buttonRef}
            type="button"
            id={props.id}
            className="hv4-sel-value"
            value={values[0] ?? ""}
            aria-label={props["aria-label"]}
            aria-haspopup="listbox"
            aria-expanded={open}
            disabled={props.disabled}
            onClick={() => (open ? handleClose() : handleOpen())}
            onKeyDown={handleKeyDown}
          >
            {singleText || <span className="hv4-sel-placeholder">{props.placeholder}</span>}
          </button>
        )}
        <ChevronDown className="hv4-sel-arrow" size={16} aria-hidden="true" />
      </div>
      {open && (
        <ul ref={menuRef} role="listbox" className="hv4-sel-menu" style={menuStyle}>
          {props.addControls && props.allowAdd && <li className="hv4-sel-controls">
            <button type="button" className="hv4-btn hv4-btn-primary" disabled={!trimmed}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => {
                const existing = options.find((item) => item.text.trim().toLowerCase() === trimmed.toLowerCase());
                const value = existing?.value ?? trimmed;
                if (!values.includes(value)) handlePick(value);
                else setQuery("");
              }}>เพิ่ม</button>
            <button type="button" className="hv4-btn hv4-btn-ghost" onClick={handleClose}>เสร็จ</button>
          </li>}
          {items.length === 0 && (
            <li className="hv4-sel-empty">{trimmed ? "ไม่พบรายการ" : "ไม่มีรายการให้เลือก"}</li>
          )}
          {items.map((item, index) => (
            <li
              key={`${item.isNew ? "new" : "opt"}_${item.value}`}
              role="option"
              aria-selected={values.includes(item.value)}
              aria-disabled={item.disabled || undefined}
              className={[
                "hv4-sel-option",
                index === active && "hv4-sel-option-active",
                values.includes(item.value) && "hv4-sel-option-picked",
                item.disabled && "hv4-sel-option-disabled",
              ]
                .filter(Boolean)
                .join(" ")}
              onMouseDown={(e) => e.preventDefault()}
              onMouseEnter={() => setActive(index)}
              onClick={() => !item.disabled && handlePick(item.value)}
            >
              {props.multiple && (
                <span className="hv4-sel-check" aria-hidden="true">
                  {values.includes(item.value) && <Check size={14} />}
                </span>
              )}
              {item.text}
              {item.preview !== undefined && item.preview !== "" && (
                <div className="hv4-sel-option-preview">{item.preview}</div>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

SelectV4.displayName = "SelectV4";

export default SelectV4;
